import { Info, Check, BadgePercent } from "lucide-react";
import { useSeo } from "@/components/Layout";
import { RevealLines, Reveal, RevealHeading, Stagger, StaggerItem } from "@/components/motion-primitives";
import { CtaButton, SectionLabel } from "@/components/ui-blocks";
import { SITE, IMAGES } from "@/data/site";
import { useLang } from "@/i18n/LanguageContext";

export default function CoachingDomicile() {
  const { t } = useLang();
  const d = t.domicile;
  useSeo(d.seoTitle, d.seoDesc);
  const showTax = Boolean(SITE.sapNumber);

  return (
    <div data-testid="coaching-domicile-page">
      {/* HERO 2-col */}
      <section className="max-w-8xl mx-auto px-5 sm:px-6 lg:px-12 pt-32 lg:pt-32 pb-12 lg:pb-16 grid lg:grid-cols-12 gap-8 lg:gap-14 items-center">
        <div className="lg:col-span-6">
          <SectionLabel>{d.label}</SectionLabel>
          <RevealLines as="h1" delay={0.1} className="display-hero text-ink mt-4" lines={d.heroLines} />
          <Reveal delay={0.4}><p className="mt-5 text-muted-foreground leading-relaxed">{d.sub}</p></Reveal>
          {showTax && (
            <Reveal delay={0.5} className="mt-4">
              <span className="inline-flex items-center gap-2 bg-accentbrand text-white text-xs font-bold uppercase tracking-wide px-3 py-2">
                <BadgePercent size={15} /> {t.common.taxBadge}
              </span>
            </Reveal>
          )}
          <Reveal delay={0.55} className="mt-6 flex flex-col sm:flex-row gap-3">
            <CtaButton to="/contact" data-testid="domicile-cta" className="w-full sm:w-auto justify-center">{d.cta}</CtaButton>
          </Reveal>
          <Reveal delay={0.6}><p className="mt-4 text-sm text-muted-foreground">{t.common.zoneShort}</p></Reveal>
        </div>
        <Reveal className="lg:col-span-6">
          <div className="relative aspect-[4/5] lg:aspect-[5/6] overflow-hidden bg-secondary max-w-md mx-auto lg:max-w-none">
            <img src={IMAGES.floor} alt={d.imgAlt} className="w-full h-full object-cover object-center" />
          </div>
        </Reveal>
      </section>

      {/* ADVANTAGES */}
      <section className="bg-secondary/50 border-y border-border">
        <div className="max-w-8xl mx-auto px-5 sm:px-6 lg:px-12 py-14 lg:py-24">
          <RevealHeading as="h2" text={d.advantagesHeading} className="display-2 text-ink mb-8" />
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" gap={0.06}>
            {d.advantages.map((a, i) => (
              <StaggerItem key={i} className="bg-card border border-border p-6">
                <span className="font-mono-alt text-[11px] text-accentbrand">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-2 font-display text-xl font-semibold text-ink">{a.title}</h3>
                <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{a.text}</p>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* TAX CREDIT — only if SAP number set */}
      {showTax && (
        <section className="max-w-8xl mx-auto px-5 sm:px-6 lg:px-12 py-14 lg:py-24" data-testid="tax-credit-section">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-14">
            <div className="lg:col-span-7">
              <SectionLabel>{d.taxLabel}</SectionLabel>
              <RevealHeading as="h2" text={d.taxHeading} className="mt-4 display-1 text-ink" />
              <Reveal delay={0.1}><p className="mt-5 text-muted-foreground leading-relaxed">{d.taxText}</p></Reveal>
              <Reveal delay={0.15}><p className="mt-3 text-muted-foreground leading-relaxed">{d.taxExplain}</p></Reveal>
              <div className="mt-6 flex items-start gap-3 bg-secondary/70 p-4 text-sm text-muted-foreground">
                <Info size={16} className="text-accentbrand mt-0.5 shrink-0" />
                <p>{d.taxMention}</p>
              </div>
              <p className="mt-4 text-sm text-ink">{d.taxSap.replace("{sap}", SITE.sapNumber)}</p>
              <p className="mt-1 text-sm text-muted-foreground">{d.taxAttestation}</p>
            </div>
            <div className="lg:col-span-5">
              <div className="border-2 border-ink p-6 bg-card">
                {d.taxExample.map((e, i) => (
                  <div key={i} className={`flex items-baseline justify-between py-3 ${i > 0 ? "border-t border-border" : ""} ${i === d.taxExample.length - 1 ? "text-accentbrand" : "text-ink"}`}>
                    <span className="text-sm text-muted-foreground">{e.label}</span>
                    <span className="font-display text-2xl font-bold">{e.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* FAQ */}
          <div className="mt-12">
            <RevealHeading as="h3" text={d.faqHeading} className="display-2 text-ink mb-6" />
            <Stagger className="grid md:grid-cols-2 gap-4" gap={0.06}>
              {d.faq.map((f, i) => (
                <StaggerItem key={i} className="border border-border bg-card p-6">
                  <h4 className="font-display text-lg font-semibold text-ink">{f.q}</h4>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
                </StaggerItem>
              ))}
            </Stagger>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <CtaButton to="/contact" data-testid="domicile-tax-cta" className="w-full sm:w-auto justify-center">{d.cta}</CtaButton>
            <CtaButton to="/contact" variant="outline" className="w-full sm:w-auto justify-center">{d.ctaSecondary}</CtaButton>
          </div>
        </section>
      )}

      {/* PROCESS */}
      <section className="bg-night text-white">
        <div className="max-w-8xl mx-auto px-5 sm:px-6 lg:px-12 py-14 lg:py-24">
          <RevealHeading as="h2" text={d.processHeading} className="display-1 mb-8" />
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8" gap={0.06}>
            {d.process.map((p, i) => (
              <StaggerItem key={i} className="flex items-start gap-4 py-4 border-t border-white/15">
                <span className="font-mono-alt text-xs text-accentbrand pt-1">{String(i + 1).padStart(2, "0")}</span>
                <div>
                  <h3 className="font-display text-lg font-semibold">{p.title}</h3>
                  <p className="mt-1 text-sm text-white/70 leading-relaxed">{p.text}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>
    </div>
  );
}
